/**
 * COMPTABILITÉ — VENTES & PRODUITS.
 *
 * Ce qui se vend, quand, et où, sur une période : les tickets des caisses ZAP
 * (la bande de contrôle des annexes « Résultats - Caisse ») lus nuit après
 * nuit. Le classement des produits et leur évolution, l'affluence heure par
 * heure, les salles, les moyens de paiement, les offerts, les litres servis.
 *
 * ═══ CE QUE CET ÉCRAN EST, ET CE QU'IL N'EST PAS ═══
 *
 * Une LECTURE des annexes, servie par `GET /api/v1/finance/sales`. Rien ici
 * n'est un chiffre arrêté : l'encaissé est celui des tickets, carte serveur
 * exclue ; le chiffre d'affaires de référence reste celui du module « Chiffre
 * d'affaires », issu des clôtures. Rien ne se saisit, rien ne se corrige —
 * sauf un geste : « Lire les tickets de cette nuit », dans la couverture,
 * quand une nuit a des détails de caisse que la lecture actuelle n'a pas
 * encore exploités (aucun effet sur une clôture).
 *
 * ═══ LA BILLETTERIE, SUR LA MÊME PAGE ═══
 *
 * « Afficher » choisit les caisses ZAP, la billetterie Shotgun, ou tout
 * (`SalesTicketing.tsx`, servie par `GET /api/v1/finance/sales/ticketing`) :
 * entrées, ventes billetterie TTC, redditions, tarifs, préventes, arrivées,
 * guichet, et la dépense au bar par entrée. Même période, résolue par le
 * serveur avec la même règle. Rien n'y attend une clôture.
 *
 * ═══ CE QUE L'ÉCRAN S'INTERDIT ═══
 *
 *   · un montant inconnu s'écrit « — », jamais 0 ; une période sans annexe
 *     lue le DIT, au lieu d'afficher des zéros ;
 *   · une caisse dont les tickets n'ont pas été lus n'est pas passée sous
 *     silence : la couverture nomme chaque nuit incomplète et sa raison ;
 *   · aucun texte coupé : les noms longs reviennent à la ligne ;
 *   · les offerts n'arrivent qu'avec leur droit : le serveur les envoie à
 *     `null` sinon, et l'écran dit pourquoi il ne les montre pas.
 *
 * ═══ LES GRAPHIQUES ═══
 *
 * Maison (CSS et SVG, aucune bibliothèque). Une seule teinte, l'accent, et un
 * gris pour le repère : la carte de chaleur est SÉQUENTIELLE (plus foncé =
 * plus de ventes, échelle affichée), la courbe compare la période à la
 * précédente (légende à deux entrées), les barres ne portent qu'une série.
 * Chaque valeur se lit aussi sans viser : le survol écrit la valeur au-dessus
 * du graphique, et chaque graphique a sa vue « tableau ».
 */

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, BarChart3, CalendarRange, CheckCircle2, Clock, Coins, CreditCard,
  Flame, Gift, GlassWater, Medal, Moon, Package, Receipt, Search, ShoppingBasket, Sparkles, Store, Ticket, Trophy,
} from 'lucide-react'
import { Link } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { LireLesTickets, type BilanRelecture } from './PosDetail.js'
import { SectionBilletterie, commeBilletterie, type LectureBilletterie } from './SalesTicketing.js'
import { DatePicker, SegmentedControl } from '../admin/pickers.js'
import { apiGet, usePermissions } from '../admin/primitives.js'
import { formatMoney } from './money.js'
import { remplacerRecherche } from './adresse-ecran.js'
import { ymdValide } from './date-adresse.js'
import { EnCours, Indisponible, aujourdhuiParis, ajouterJours, nuitCourte, nuitLongue } from './ClosingCommon.js'
import './finance.css'
import './finance-sales.css'

/* ═══════════════════════ les formes lues (miroir du cœur, `finance/sales.ts`) ═══════════════════════ */

interface Cellule { tickets: number; revenue_cents: number }

interface Produit {
  product: string
  qty: number
  revenue_cents: number | null
  gross_cents: number | null
  non_revenue_cents: number | null
  unit_prices_cents: number[]
  dosed_qty: number
  share_qty: number | null
  share_revenue: number | null
  previous_qty: number | null
  previous_revenue_cents: number | null
  qty_change: number | null
  is_new: boolean
}

interface Kpis {
  revenue_cents: number | null
  tickets: number
  /** `null` sans le droit des offerts : tickets − payants = tickets entièrement offerts. */
  paying_tickets: number | null
  average_ticket_cents: number | null
  articles: number
  articles_per_ticket: number | null
  biggest_ticket_cents: number | null
  offers_cents: number | null
  offered_tickets: number | null
  nights_with_sales: number
  revenue_per_night_cents: number | null
}

interface Ventes {
  period: { from: string; to: string; days: number; preset: string | null }
  previous: { from: string; to: string; has_data: boolean }
  scope: 'BARS' | 'VESTIAIRE' | 'TOUT'
  latest_night: string | null
  season: { from: string; to: string } | null
  has_data: boolean
  kpis: Kpis
  previous_kpis: Kpis | null
  top: Produit[]
  products: Produit[]
  slots: Array<{ hour: number; label: string }>
  by_night: Array<{ business_date: string; weekday: number; soirees: string[]; tickets: number; revenue_cents: number; cells: Cellule[] }>
  by_weekday: Array<{ weekday: number; nights: number; tickets: number; revenue_cents: number; cells: Cellule[] }>
  curve: Array<{ hour: number; label: string; tickets: number; revenue_cents: number; previous_tickets: number | null; previous_revenue_cents: number | null }>
  untimed_tickets: number
  insights: {
    peak: { hour: number; label: string; tickets: number; tickets_per_night: number; share: number } | null
    record: { business_date: string; label: string; tickets: number; revenue_cents: number; soirees: string[] } | null
    half_by: string | null
    best_night: { business_date: string; soirees: string[]; revenue_cents: number; tickets: number } | null
    shooters: { products: number; qty: number; share_qty: number | null; revenue_cents: number | null; share_revenue: number | null } | null
    dosed_share: number | null
  }
  rooms: Array<{
    location_id: string; name: string; revenue_cents: number; tickets: number; articles: number; share_revenue: number | null
    star: { product: string; qty: number } | null
    points_of_sale: Array<{
      id: string; name: string; kind: string; revenue_cents: number; tickets: number; articles: number
      average_ticket_cents: number | null; share_revenue: number | null
      registers: Array<{ register_id: string; name: string; zap_labels: string[]; revenue_cents: number; tickets: number }>
    }>
  }>
  unassigned: Array<{ zap_label: string; tickets: number; revenue_cents: number }>
  payments: Array<{ label: string; cents: number; tickets: number; revenue: boolean | null; share: number | null }>
  offers: { total_cents: number; tickets: number; by_product: Array<{ product: string; cents: number; share: number | null }> } | null
  spirits: {
    rampes: number; nights: number; total_doses: number; total_volume_l: number; complete: boolean
    products: Array<{ product: string; doses: number; volume_l: number | null; dose_cl: number[]; bottle_changes: number; nights: number }>
  } | null
  coverage: {
    nights: Array<{
      business_date: string; soirees: string[]; sessions: number; sessions_read: number; tickets: number
      problems: Array<{ register: string; zap_label: string; reason: string }>
      /** Détails de caisse de la nuit qu'une relecture compléterait (absent d'un serveur plus ancien : 0). */
      rereadable?: number
    }>
    sessions: number; sessions_read: number; nights_with_sales: number; nights_without_sales: number
  }
}

const objet = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null

/** Garde minimale : on vérifie l'identité de la forme, pas chaque champ. */
const commeVentes = (v: unknown): Ventes | null => {
  const o = objet(v)
  return o && objet(o['kpis']) && Array.isArray(o['products']) && Array.isArray(o['slots']) && objet(o['coverage']) ? (o as unknown as Ventes) : null
}

/* ═══════════════════════ petits outils d'écriture ═══════════════════════ */

/** Un montant par `formatMoney`, insécable : « 474,00 € » ne se coupe jamais avant l'euro. */
const montant = (c: number | null): string => formatMoney(c).replace(/ €$/, ' €')
const nombre = (n: number): string => n.toLocaleString('fr-FR')
const decimal = (n: number): string => n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })
const pluriel = (n: number, un: string, plusieurs = `${un}s`): string => `${nombre(n)} ${n > 1 ? plusieurs : un}`
/** « 12 % », « 4,5 % » sous 10 % ; `null` → « — ». */
const pourcent = (p: number | null): string =>
  p === null ? '—' : `${(p * 100).toLocaleString('fr-FR', { maximumFractionDigits: p < 0.1 ? 1 : 0 })} %`
/** « ven. 3 oct. », en minuscule dans une phrase. */
const nuitDansPhrase = (ymd: string): string => nuitCourte(ymd).toLocaleLowerCase('fr')
const litres = (l: number): string => `${l.toLocaleString('fr-FR', { maximumFractionDigits: l < 10 ? 2 : 1 })} l`
const JOURS = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

type Periode = 'derniere' | '7j' | '30j' | 'saison' | 'perso'
type Perimetre = 'bars' | 'vestiaire' | 'tout'
/** Ce que la page montre : les caisses ZAP, la billetterie Shotgun, ou les deux. */
type Vue = 'caisses' | 'billetterie' | 'tout'
type Mesure = 'tickets' | 'montant'
type Tri = 'qty' | 'montant' | 'evolution' | 'nom'

const PERIODES: Array<{ value: Periode; label: string }> = [
  { value: 'derniere', label: 'Dernière soirée' },
  { value: '7j', label: '7 jours' },
  { value: '30j', label: '30 jours' },
  { value: 'saison', label: 'Saison' },
  { value: 'perso', label: 'Personnalisé' },
]
const VUES: Array<{ value: Vue; label: string }> = [
  { value: 'caisses', label: 'Bars et caisses' },
  { value: 'billetterie', label: 'Billetterie' },
  { value: 'tout', label: 'Tout' },
]
const PERIMETRES: Array<{ value: Perimetre; label: string }> = [
  { value: 'bars', label: 'Bars' },
  { value: 'vestiaire', label: 'Vestiaire' },
  { value: 'tout', label: 'Toutes les caisses' },
]

/** L'adresse porte la lecture : recharger ou partager le lien retombe sur la même période. */
function etatInitial(): { periode: Periode; du: string | null; au: string | null; perimetre: Perimetre; vue: Vue } {
  const q = new URLSearchParams(window.location.search)
  const p = q.get('periode')
  const du = q.get('du')
  const au = q.get('au')
  const pe = q.get('perimetre')
  const vu = q.get('vue')
  const perimetre: Perimetre = pe === 'vestiaire' || pe === 'tout' ? pe : 'bars'
  // Tout, par défaut : la billetterie et les caisses sur la même page.
  const vue: Vue = vu === 'caisses' || vu === 'billetterie' ? vu : 'tout'
  // Deux dates RÉELLES, dans l'ordre : « 2026-13-01 » faisait lever le premier rendu.
  if (p === 'perso' && ymdValide(du) && ymdValide(au) && du <= au) return { periode: 'perso', du, au, perimetre, vue }
  const periode: Periode = p === '7j' || p === '30j' || p === 'saison' ? p : 'derniere'
  return { periode, du: null, au: null, perimetre, vue }
}

/** « samedi 3 octobre 2026 », ou « du ven. 4 sept. au sam. 3 oct. 2026 ». */
function libellePeriode(from: string, to: string): string {
  if (from === to) return nuitLongue(from)
  return `Du ${nuitDansPhrase(from)} au ${nuitDansPhrase(to)} ${to.slice(0, 4)}`
}

/**
 * CE À QUOI LA PÉRIODE EST COMPARÉE, dit en clair et accordé à sa phrase.
 * Une nuit se compare à la même nuit une semaine plus tôt ; moins de sept
 * nuits, aux mêmes nuits une semaine plus tôt ; au-delà, aux nuits d'avant.
 *   · `a`   : « par rapport AU sam. 26 sept. », « … AUX 7 nuits d’avant » ;
 *   · `sur` : « 57 par nuit LE sam. 26 sept. », « … SUR LES 7 nuits d’avant » ;
 *   · `nu`  : la légende — « le sam. 26 sept. », « les 7 nuits d’avant ».
 */
function libelleAvant(v: { previous: { from: string; to: string }; period: { days: number } }, forme: 'a' | 'sur' | 'nu' = 'nu'): string {
  const { from, to } = v.previous
  if (from === to) {
    const nuit = nuitDansPhrase(from)
    return forme === 'a' ? `au ${nuit}` : `le ${nuit}`
  }
  const nuits = v.period.days < 7 ? 'mêmes nuits une semaine plus tôt' : `${pluriel(v.period.days, 'nuit')} d’avant`
  return forme === 'a' ? `aux ${nuits}` : forme === 'sur' ? `sur les ${nuits}` : `les ${nuits}`
}

/* ═══════════════════════════════ la page ═══════════════════════════════ */

interface Lecture { donnees: Ventes | null; chargement: boolean; erreur: { titre: string; message: string; reglages: boolean } | null }

export function FinanceSalesPage() {
  const initial = useMemo(etatInitial, [])
  const [periode, setPeriode] = useState<Periode>(initial.periode)
  const [du, setDu] = useState<string>(initial.du ?? ajouterJours(aujourdhuiParis(), -6))
  const [au, setAu] = useState<string>(initial.au ?? aujourdhuiParis())
  const [perimetre, setPerimetre] = useState<Perimetre>(initial.perimetre)
  const [vue, setVue] = useState<Vue>(initial.vue)
  const [lecture, setLecture] = useState<Lecture>({ donnees: null, chargement: true, erreur: null })
  const [billets, setBillets] = useState<LectureBilletterie>({ etat: 'chargement' })
  /* Une relecture des tickets d'une nuit relance les deux lectures ; ses
     bilans restent affichés, nuit par nuit, après le rechargement. */
  const [version, setVersion] = useState(0)
  const [bilans, setBilans] = useState<Record<string, BilanRelecture>>({})
  const { permissions, loading: droitsEnCours } = usePermissions()
  const voitOfferts = !droitsEnCours && permissions.includes('finance.offers.read')
  const voitBilletterie = !droitsEnCours && permissions.includes('finance.live.read')
  const peutRelire = !droitsEnCours && permissions.includes('finance.source.manage')
  const avecCaisses = vue !== 'billetterie'
  const avecBilletterie = vue !== 'caisses'

  const bornes = periode === 'perso' ? `from=${du}&to=${au}` : `periode=${periode}`
  const chemin = `/api/v1/finance/sales?${bornes}&perimetre=${perimetre}`
  const cheminBillets = `/api/v1/finance/sales/ticketing?${bornes}`

  // L'adresse suit la lecture ; elle est REMPLACÉE, jamais empilée — sans quoi
  // chaque clic sur une période empilerait un retour inutile. Pas `redirect` :
  // réécrite dès le montage, elle empêchait la page de s'ouvrir en haut
  // (voir adresse-ecran.ts).
  useEffect(() => {
    const q = new URLSearchParams({ periode })
    if (periode === 'perso') { q.set('du', du); q.set('au', au) }
    q.set('perimetre', perimetre)
    q.set('vue', vue)
    remplacerRecherche('/comptabilite/ventes', q)
  }, [periode, du, au, perimetre, vue])

  useEffect(() => {
    // La couverture de la billetterie seule n'a pas besoin des caisses, mais
    // la période affichée (et ses bornes réelles) vient de cette lecture :
    // elle est faite dans les trois vues.
    let vivant = true
    // La lecture précédente reste affichée, estompée, pendant le chargement :
    // pas de squelette, pas de saut de mise en page.
    setLecture((l) => ({ ...l, chargement: true }))
    void (async () => {
      let suite: Lecture
      try {
        const brut = await apiGet<unknown>(chemin)
        const o = objet(brut)
        const code = o && typeof o['error'] === 'string' ? o['error'] : null
        const v = code === null ? commeVentes(brut) : null
        if (v) suite = { donnees: v, chargement: false, erreur: null }
        else if (code === 'FORBIDDEN' || code === 'UNAUTHENTICATED') {
          suite = { donnees: null, chargement: false, erreur: { titre: 'Les ventes ne sont pas accessibles', message: 'Votre rôle ne donne pas accès aux montants encaissés : cette lecture demande le droit de consulter le chiffre d’affaires.', reglages: false } }
        } else {
          const message = o && typeof o['message'] === 'string' ? o['message'] : 'Cette information n’est pas encore servie par le serveur.'
          suite = { donnees: null, chargement: false, erreur: { titre: 'Cette période ne peut pas être lue', message, reglages: /Règles & automatisations/.test(message) } }
        }
      } catch {
        suite = { donnees: null, chargement: false, erreur: { titre: 'Les ventes n’ont pas pu être lues', message: 'Gestion n’a pas pu joindre le serveur. Réessayez dans un instant.', reglages: false } }
      }
      if (vivant) setLecture(suite)
    })()
    return () => { vivant = false }
  }, [chemin, version])

  useEffect(() => {
    if (!avecBilletterie || droitsEnCours) return
    if (!voitBilletterie) { setBillets({ etat: 'interdit' }); return }
    let vivant = true
    setBillets((b) => (b.etat === 'ok' ? { ...b, rechargement: true } : { etat: 'chargement' }))
    void (async () => {
      let suite: LectureBilletterie
      try {
        const brut = await apiGet<unknown>(cheminBillets)
        const o = objet(brut)
        const code = o && typeof o['error'] === 'string' ? o['error'] : null
        const v = code === null ? commeBilletterie(brut) : null
        if (v) suite = { etat: 'ok', valeur: v, rechargement: false }
        else if (code === 'FORBIDDEN') suite = { etat: 'interdit' }
        else {
          const message = o && typeof o['message'] === 'string' ? o['message'] : 'Cette information n’est pas encore servie par le serveur.'
          suite = { etat: 'erreur', titre: 'La billetterie de cette période ne peut pas être lue', message }
        }
      } catch {
        suite = { etat: 'erreur', titre: 'La billetterie n’a pas pu être lue', message: 'Gestion n’a pas pu joindre le serveur. Réessayez dans un instant.' }
      }
      if (vivant) setBillets(suite)
    })()
    return () => { vivant = false }
  }, [cheminBillets, avecBilletterie, voitBilletterie, droitsEnCours, version])

  const d = lecture.donnees
  const vestiaire = perimetre === 'vestiaire'
  const relu = (b: BilanRelecture): void => {
    setBilans((x) => ({ ...x, [b.business_date]: b }))
    setVersion((v) => v + 1)
  }

  const choisirPeriode = (p: Periode): void => {
    // « Personnalisé » part de la période affichée : on ajuste, on ne recommence pas.
    if (p === 'perso' && d) { setDu(d.period.from); setAu(d.period.to) }
    setPeriode(p)
  }
  const choisirDu = (ymd: string): void => { if (ymd > au) { setDu(au); setAu(ymd) } else setDu(ymd) }
  const choisirAu = (ymd: string): void => { if (ymd < du) { setAu(du); setDu(ymd) } else setAu(ymd) }

  const caissesZap = d ? (
    <div className={`fsv-contenu${lecture.chargement ? ' fsv-contenu--recharge' : ''}`} aria-busy={lecture.chargement}>
      <Couverture d={d} peutRelire={peutRelire} bilans={bilans} onRelu={relu} />

      {!d.has_data ? (
        <section className="lc-panel">
          <div className="fi-empty">
            <div className="fi-empty__title">Aucune annexe lue pour cette période</div>
            <p className="fsv-vide__texte">
              {d.coverage.sessions > 0
                ? 'Des détails de caisse ZAP sont arrivés pour ces nuits, mais leurs tickets n’ont pas pu être lus : la liste ci-dessus dit pourquoi, caisse par caisse.'
                : 'Aucun détail de caisse ZAP n’est arrivé pour ces nuits. Les ventes apparaîtront ici dès que les rapports « Résultats - Caisse » seront reçus et lus.'}
            </p>
            {d.latest_night && (d.period.from !== d.latest_night || d.period.to !== d.latest_night) ? (
              <button type="button" className="lc-btn lc-btn--sm" onClick={() => { setDu(d.latest_night!); setAu(d.latest_night!); setPeriode('perso') }}>
                Voir la dernière nuit reçue, {nuitDansPhrase(d.latest_night)}
              </button>
            ) : null}
          </div>
        </section>
      ) : (
        <>
          <Indicateurs d={d} voitOfferts={voitOfferts} vestiaire={vestiaire} />

          <div className="fsv-grille fsv-grille--podium">
            <Podium d={d} vestiaire={vestiaire} />
            <ARetenir d={d} vestiaire={vestiaire} />
          </div>

          <Affluence d={d} vestiaire={vestiaire} />

          <div className="fsv-grille fsv-grille--produits">
            <Classement d={d} voitOfferts={voitOfferts} />
            <div className="fsv-pile">
              <Paiements d={d} voitOfferts={voitOfferts} />
              <Offerts d={d} voitOfferts={voitOfferts} />
              <Spiritueux d={d} vestiaire={vestiaire} />
            </div>
          </div>

          <Salles d={d} vestiaire={vestiaire} />
        </>
      )}
    </div>
  ) : null

  return (
    <main className="lc-page lc-page--large lc-stagger fsv-page">
      <SmartBack />
      <header className="fh-hero">
        <span className="fh-hero__icon" aria-hidden="true"><ShoppingBasket size={26} /></span>
        <div>
          <h1 className="lc-title">Ventes &amp; produits</h1>
          <p className="lc-subtitle fsv-sous-titre">
            Ce qui se vend, à quelle heure et dans quelle salle, d’après les tickets des caisses ZAP et la billetterie Shotgun.
            Une lecture des rapports : rien ici n’attend une clôture, ni ne la change.
          </p>
        </div>
      </header>

      {/* ═══════════ la période, la vue et le périmètre : une seule rangée, au-dessus de tout ═══════════ */}
      <section className="lc-panel fsv-barre" aria-label="Période, vue et périmètre">
        <div className="fsv-barre__corps">
          <div className="fsv-barre__groupe">
            <span className="fsv-barre__label">Période</span>
            <div className="fsv-seg">
              <SegmentedControl value={periode} onChange={choisirPeriode} options={PERIODES} ariaLabel="Période lue" size="sm" />
            </div>
            {periode === 'perso' ? (
              <div className="fsv-dates">
                <span className="fsv-dates__mot">du</span>
                <DatePicker value={du} onPick={choisirDu} label={nuitCourte(du)} highlightFrom={du} highlightTo={au} ariaLabel="Première nuit de la période" />
                <span className="fsv-dates__mot">au</span>
                <DatePicker value={au} onPick={choisirAu} label={nuitCourte(au)} highlightFrom={du} highlightTo={au} ariaLabel="Dernière nuit de la période" />
              </div>
            ) : null}
          </div>
          <div className="fsv-barre__groupe">
            <span className="fsv-barre__label">Afficher</span>
            <div className="fsv-seg">
              <SegmentedControl value={vue} onChange={setVue} options={VUES} ariaLabel="Ce que la page affiche" size="sm" />
            </div>
          </div>
          {avecCaisses ? (
            <div className="fsv-barre__groupe">
              <span className="fsv-barre__label">Caisses</span>
              <div className="fsv-seg">
                <SegmentedControl value={perimetre} onChange={setPerimetre} options={PERIMETRES} ariaLabel="Caisses lues" size="sm" />
              </div>
            </div>
          ) : null}
        </div>
        {d ? (
          <p className="fsv-barre__lecture">
            <CalendarRange size={15} aria-hidden="true" />
            <strong>{libellePeriode(d.period.from, d.period.to)}</strong>
            {d.period.days > 1 ? <span>{pluriel(d.period.days, 'nuit')}</span> : null}
            <span className="fsv-barre__avant">comparé {d.previous.from === d.previous.to ? `au ${nuitDansPhrase(d.previous.from)}` : `à la période du ${nuitDansPhrase(d.previous.from)} au ${nuitDansPhrase(d.previous.to)}`}</span>
          </p>
        ) : null}
      </section>

      {lecture.chargement && !d && !lecture.erreur ? <EnCours quoi="des ventes" /> : null}
      {lecture.erreur ? (
        <section className="lc-panel">
          <Indisponible titre={lecture.erreur.titre} message={lecture.erreur.message}>
            {lecture.erreur.reglages ? <Link className="lc-btn lc-btn--sm" href="/comptabilite/regles">Ouvrir les règles</Link> : null}
          </Indisponible>
        </section>
      ) : null}

      {/* La porte d'abord, le bar ensuite : la nuit se lit dans cet ordre. */}
      {avecBilletterie && !lecture.erreur ? (
        <SectionBilletterie lecture={billets}
          contre={billets.etat === 'ok' ? libelleAvant(billets.valeur, 'a') : ''}
          contreSur={billets.etat === 'ok' ? libelleAvant(billets.valeur, 'sur') : ''} />
      ) : null}

      {avecCaisses && caissesZap ? (
        vue === 'tout' ? (
          <section className="fsv-partie" aria-labelledby="fsv-caisses-titre">
            <header className="fsv-partie__tete">
              <span className="fsv-partie__icone" aria-hidden="true"><ShoppingBasket size={22} /></span>
              <div className="fsv-partie__texte">
                <h2 className="fsv-partie__titre" id="fsv-caisses-titre">Les caisses ZAP</h2>
                <p className="fsv-partie__sous">Les tickets des bars et du vestiaire : produits, heures, salles, moyens de paiement, offerts, litres servis.</p>
              </div>
            </header>
            {caissesZap}
          </section>
        ) : caissesZap
      ) : null}
    </main>
  )
}

/* ═══════════════════════════════ la couverture ═══════════════════════════════ */

/**
 * CE QUI A ÉTÉ LU. Les chiffres de la page ne valent que pour les caisses
 * dont les tickets sont lus : la couverture le dit d'un regard (« 12 nuits
 * lues, 1 incomplète ») et nomme, nuit par nuit, chaque caisse manquante et
 * sa raison. Quand une relecture peut combler le manque, la nuit porte le
 * bouton « Lire les tickets de cette nuit » — et la liste s'ouvre d'elle-même,
 * pour que le bouton ne reste pas caché sous un repli.
 */
function Couverture(props: { d: Ventes; peutRelire: boolean; bilans: Record<string, BilanRelecture>; onRelu: (b: BilanRelecture) => void }) {
  const { d } = props
  const c = d.coverage
  const incompletes = c.nights.filter((n) => n.problems.length > 0 || n.sessions === 0 || (n.rereadable ?? 0) > 0)
  const aLire = incompletes.some((n) => (n.rereadable ?? 0) > 0)
  /* Les nuits relues pendant la visite et désormais complètes : leur bilan reste lisible. */
  const relues = Object.values(props.bilans)
    .filter((b) => b.business_date >= d.period.from && b.business_date <= d.period.to && !incompletes.some((n) => n.business_date === b.business_date))
    .sort((x, y) => x.business_date.localeCompare(y.business_date))
  const tout = incompletes.length === 0 && d.unassigned.length === 0
  return (
    <section className={`lc-panel fsv-couv${tout ? '' : ' fsv-couv--reserve'}`} aria-labelledby="fsv-couv-titre">
      <div className="fsv-couv__tete">
        <span className={`fsv-couv__icone${tout ? '' : ' fsv-couv__icone--reserve'}`} aria-hidden="true">
          {tout ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
        </span>
        <div className="fsv-couv__texte">
          <h2 className="fsv-couv__titre" id="fsv-couv-titre">
            {c.sessions === 0 && c.nights.length === 0
              ? 'Aucun détail de caisse reçu pour cette période'
              : tout
                ? `Toutes les caisses reçues sont lues : ${pluriel(c.sessions_read, 'session de caisse', 'sessions de caisse')} sur ${pluriel(c.nights_with_sales, 'nuit')}`
                : `${pluriel(c.sessions_read, 'session de caisse lue', 'sessions de caisse lues')} sur ${nombre(c.sessions)} — ${pluriel(incompletes.length, 'nuit incomplète', 'nuits incomplètes')}`}
          </h2>
          <p className="fsv-couv__aide">
            Les chiffres ci-dessous ne comptent que les tickets lus. Une caisse dont les tickets manquent n’y est pas : elle est nommée ici, avec la raison.
          </p>
        </div>
      </div>
      {relues.length > 0 ? (
        <div className="fsv-couv__relues">
          {relues.map((b) => (
            <div key={b.business_date} className="fsv-couv__relue">
              <span className="fsv-couv__date">{nuitCourte(b.business_date)}</span>
              <LireLesTickets compact nuit={b.business_date} aRelire={0} peutRelire={props.peutRelire} bilan={b} onBilan={props.onRelu} />
            </div>
          ))}
        </div>
      ) : null}
      {incompletes.length > 0 || d.unassigned.length > 0 ? (
        <details className="fsv-couv__detail" open={aLire || undefined}>
          <summary>Voir les nuits incomplètes et les caisses non rangées</summary>
          <ul className="fsv-couv__nuits">
            {incompletes.map((n) => (
              <li key={n.business_date}>
                <div className="fsv-couv__nuit">
                  <Link href={`/comptabilite/nuits/${n.business_date}`} className="fsv-couv__date">{nuitCourte(n.business_date)}</Link>
                  {n.soirees.length > 0 ? <span className="fsv-couv__soirees">{n.soirees.join(' · ')}</span> : null}
                  <span className="fsv-couv__etat">
                    {n.sessions === 0
                      ? (n.rereadable ?? 0) > 0 ? 'des détails de caisse sont arrivés, mais n’ont pas encore été lus' : 'aucun détail de caisse reçu pour cette soirée'
                      : `${nombre(n.sessions_read)} ${n.sessions_read > 1 ? 'sessions lues' : 'session lue'} sur ${nombre(n.sessions)}`}
                  </span>
                </div>
                {n.problems.length > 0 ? (
                  <ul className="fsv-couv__problemes">
                    {n.problems.map((p, i) => (
                      <li key={`${p.zap_label}-${i}`}>
                        <span className="fsv-couv__caisse">{p.register}{p.register !== p.zap_label ? <span className="fsv-couv__alias">libellé du rapport : {p.zap_label}</span> : null}</span>
                        <span className="fsv-couv__raison">{p.reason}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {(n.rereadable ?? 0) > 0 || props.bilans[n.business_date] ? (
                  <LireLesTickets compact nuit={n.business_date} aRelire={n.rereadable ?? 0} peutRelire={props.peutRelire}
                    bilan={props.bilans[n.business_date] ?? null} onBilan={props.onRelu} />
                ) : null}
              </li>
            ))}
            {d.unassigned.map((u) => (
              <li key={u.zap_label}>
                <div className="fsv-couv__nuit">
                  <span className="fsv-couv__date">{u.zap_label}</span>
                  <span className="fsv-couv__chiffres">{pluriel(u.tickets, 'ticket')} · {montant(u.revenue_cents)}</span>
                </div>
                <p className="fsv-couv__raison">
                  Cette caisse du rapport n’est rattachée à aucune caisse de Gestion : ses ventes ne comptent que dans « Toutes les caisses », sans salle.
                  Rattachez-la dans les <Link href="/comptabilite/referentiels">référentiels</Link>.
                </p>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}

/* ═══════════════════════════════ les indicateurs ═══════════════════════════════ */

/** L'évolution d'un indicateur contre la période précédente — signe écrit, jamais la couleur seule. */
function Delta(props: { actuel: number | null; avant: number | null | undefined; contre: string; direSiRien?: boolean }) {
  const { actuel, avant } = props
  if (actuel === null || avant === null || avant === undefined || avant <= 0) {
    // « Rien à comparer » se dit une fois, sur la première tuile : répété
    // quatre fois, il deviendrait du bruit.
    if (!props.direSiRien) return null
    return <span className="fsv-delta fsv-delta--neutre">rien à comparer{'\u00a0'}: aucune vente lue sur la période de comparaison</span>
  }
  const pct = Math.round(((actuel - avant) / avant) * 100)
  const sens = pct > 0 ? 'hausse' : pct < 0 ? 'baisse' : 'stable'
  return (
    <span className={`fsv-delta fsv-delta--${sens}`}>
      {sens === 'hausse' ? <ArrowUpRight size={14} aria-hidden="true" /> : sens === 'baisse' ? <ArrowDownRight size={14} aria-hidden="true" /> : null}
      <strong>{pct > 0 ? '+' : pct < 0 ? '−' : ''}{nombre(Math.abs(pct))}&nbsp;%</strong> par rapport {props.contre}
    </span>
  )
}

function Tuile(props: { icone: ReactNode; label: string; valeur: string; sous?: ReactNode; delta?: ReactNode; accent?: boolean }) {
  return (
    <div className={`fsv-tuile${props.accent ? ' fsv-tuile--accent' : ''}`}>
      <span className="fsv-tuile__label"><span aria-hidden="true">{props.icone}</span>{props.label}</span>
      <span className="fsv-tuile__valeur">{props.valeur}</span>
      {props.sous ? <span className="fsv-tuile__sous">{props.sous}</span> : null}
      {props.delta ?? null}
    </div>
  )
}

function Indicateurs(props: { d: Ventes; voitOfferts: boolean; vestiaire: boolean }) {
  const { d } = props
  const k = d.kpis
  const p = d.previous_kpis
  const contre = libelleAvant(d, 'a')
  return (
    <section className="fsv-tuiles" aria-label="L’essentiel de la période">
      <Tuile accent icone={<Receipt size={15} />} label="Encaissé" valeur={montant(k.revenue_cents)}
        sous="d’après les tickets, carte serveur exclue"
        delta={<Delta actuel={k.revenue_cents} avant={p?.revenue_cents} contre={contre} direSiRien />} />
      <Tuile icone={<Ticket size={15} />} label={props.vestiaire ? 'Passages au comptoir' : 'Tickets'} valeur={nombre(k.tickets)}
        sous={d.untimed_tickets > 0 ? `dont ${pluriel(d.untimed_tickets, 'ticket')} sans heure lue` : k.paying_tickets === null ? undefined : pluriel(k.paying_tickets, 'ticket payant', 'tickets payants')}
        delta={<Delta actuel={k.tickets} avant={p?.tickets} contre={contre} />} />
      <Tuile icone={<BarChart3 size={15} />} label="Panier moyen" valeur={montant(k.average_ticket_cents)} sous="par ticket payant"
        delta={<Delta actuel={k.average_ticket_cents} avant={p?.average_ticket_cents} contre={contre} />} />
      <Tuile icone={<Package size={15} />} label="Articles vendus" valeur={nombre(k.articles)}
        sous={k.articles_per_ticket !== null ? `${decimal(k.articles_per_ticket)} par ticket en moyenne` : undefined}
        delta={<Delta actuel={k.articles} avant={p?.articles} contre={contre} />} />
      {props.voitOfferts && k.offers_cents !== null ? (
        <Tuile icone={<Gift size={15} />} label="Offerts" valeur={montant(k.offers_cents)}
          sous={`${pluriel(k.offered_tickets ?? 0, 'ticket réglé', 'tickets réglés')} par carte serveur, hors encaissé`} />
      ) : null}
      <Tuile icone={<Moon size={15} />} label="Encaissé par nuit" valeur={montant(k.revenue_per_night_cents)}
        sous={`en moyenne, sur ${pluriel(k.nights_with_sales, 'nuit')} avec des ventes`} />
    </section>
  )
}

/* ═══════════════════════════════ le podium, ce qu'il faut retenir ═══════════════════════════════ */

/** L'évolution d'un produit : « nouveau », « +25 % », « −10 % » — le signe est écrit, la flèche le redit. */
function Evolution(props: { p: Produit }) {
  const { p } = props
  if (p.is_new) {
    return <span className="fsv-evo fsv-evo--nouveau" title="Vendu sur la période, pas sur la précédente"><Sparkles size={13} aria-hidden="true" />nouveau</span>
  }
  if (p.qty_change === null) return null
  const pct = Math.round(p.qty_change * 100)
  const sens = pct > 0 ? 'hausse' : pct < 0 ? 'baisse' : 'stable'
  return (
    <span className={`fsv-evo fsv-evo--${sens}`} title={`${nombre(p.previous_qty ?? 0)} vendus sur la période précédente`}>
      {sens === 'hausse' ? <ArrowUpRight size={13} aria-hidden="true" /> : sens === 'baisse' ? <ArrowDownRight size={13} aria-hidden="true" /> : null}
      {pct > 0 ? '+' : pct < 0 ? '−' : ''}{nombre(Math.abs(pct))}&nbsp;%
    </span>
  )
}

function Tete(props: { icone: ReactNode; titre: string; id: string; children?: ReactNode }) {
  return (
    <div className="lc-panel__head fsv-tete">
      <span className="fsv-tete__icone" aria-hidden="true">{props.icone}</span>
      <h2 className="lc-panel__title fsv-tete__titre" id={props.id}>{props.titre}</h2>
      {props.children ? <div className="fsv-tete__outils">{props.children}</div> : null}
    </div>
  )
}

/**
 * LE PODIUM — les trois produits les plus vendus, en quantité. La hauteur des
 * marches est PROPORTIONNELLE aux quantités (pas un décor de podium) : un
 * premier très loin devant se voit loin devant.
 */
function Podium(props: { d: Ventes; vestiaire: boolean }) {
  const top = props.d.top
  const max = Math.max(1, ...top.map((p) => p.qty))
  return (
    <section className="lc-panel fsv-podium" aria-labelledby="fsv-podium-titre">
      <Tete icone={<Trophy size={17} />} titre="Le podium des produits" id="fsv-podium-titre">
        <span className="lc-hint">en quantité vendue</span>
      </Tete>
      <div className="lc-panel__body">
        {top.length === 0 ? <p className="lc-hint">Aucun produit relevé sur la période.</p> : (
          <ol className="fsv-podium__marches">
            {top.map((p, i) => (
              <li key={p.product} className={`fsv-marche fsv-marche--${i + 1}`}>
                <div className="fsv-marche__texte">
                  <span className="fsv-marche__nom">{p.product}</span>
                  <span className="fsv-marche__qte">{pluriel(p.qty, props.vestiaire ? 'passage' : 'vendu', props.vestiaire ? 'passages' : 'vendus')}</span>
                  <span className="fsv-marche__detail">{montant(p.revenue_cents)} encaissés · {pourcent(p.share_qty)} des articles</span>
                  <Evolution p={p} />
                </div>
                <div className="fsv-marche__socle" style={{ '--fsv-haut': `${Math.max(16, Math.round((p.qty / max) * 100))}%` } as CSSProperties}>
                  <span className="fsv-marche__rang"><Medal size={15} aria-hidden="true" />{i + 1}</span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  )
}

/** Ce que la période dit d'elle-même — une phrase par fait, la conséquence d'abord. */
function ARetenir(props: { d: Ventes; vestiaire: boolean }) {
  const { d } = props
  const i = d.insights
  const unite = props.vestiaire ? 'passages' : 'tickets'
  const faits: Array<{ cle: string; icone: ReactNode; texte: ReactNode }> = []
  if (i.peak) {
    faits.push({ cle: 'pointe', icone: <Flame size={16} />, texte: (
      <>Le créneau le plus chargé est <strong>{i.peak.label}</strong> : {pourcent(i.peak.share)} des {unite}, soit {decimal(i.peak.tickets_per_night)} par nuit en moyenne.</>
    ) })
  }
  if (i.half_by) {
    faits.push({ cle: 'moitie', icone: <Clock size={16} />, texte: <>La moitié des {unite} est encaissée <strong>avant {i.half_by}</strong>.</> })
  }
  if (i.best_night && d.by_night.length > 1) {
    faits.push({ cle: 'nuit', icone: <Trophy size={16} />, texte: (
      <>La nuit la plus forte : <strong>{nuitDansPhrase(i.best_night.business_date)}</strong>{i.best_night.soirees.length > 0 ? ` (${i.best_night.soirees.join(' · ')})` : ''}, {montant(i.best_night.revenue_cents)} encaissés en {pluriel(i.best_night.tickets, 'ticket')}.</>
    ) })
  }
  if (i.record) {
    faits.push({ cle: 'record', icone: <Ticket size={16} />, texte: (
      <>L’heure la plus intense : <strong>{i.record.label}</strong>{d.by_night.length > 1 ? ` le ${nuitDansPhrase(i.record.business_date)}` : ''}, {pluriel(i.record.tickets, 'ticket')} pour {montant(i.record.revenue_cents)}.</>
    ) })
  }
  if (i.shooters && !props.vestiaire) {
    faits.push({ cle: 'shooters', icone: <GlassWater size={16} />, texte: (
      <>Les shooters font <strong>{pourcent(i.shooters.share_qty)}</strong> des articles et {pourcent(i.shooters.share_revenue)} de l’encaissé<span className="fsv-fait__note">produits dont le nom contient « shooter » ({pluriel(i.shooters.products, 'référence')})</span></>
    ) })
  }
  if (i.dosed_share !== null && i.dosed_share > 0) {
    faits.push({ cle: 'doseur', icone: <GlassWater size={16} />, texte: <><strong>{pourcent(i.dosed_share)}</strong> des articles sont servis au doseur.</> })
  }
  const stars = d.rooms.filter((r) => r.star !== null)
  if (stars.length > 1) {
    faits.push({ cle: 'stars', icone: <Store size={16} />, texte: (
      <>Le produit star de chaque salle :
        <span className="fsv-fait__liste">{stars.map((r) => <span key={r.location_id}><strong>{r.name}</strong> — {r.star!.product} ({nombre(r.star!.qty)})</span>)}</span>
      </>
    ) })
  }
  const nouveaux = d.products.filter((p) => p.is_new).length
  if (nouveaux > 0) {
    faits.push({ cle: 'nouveaux', icone: <Sparkles size={16} />, texte: <>{pluriel(nouveaux, 'produit vendu', 'produits vendus')} sur la période ne {nouveaux > 1 ? 'l’étaient' : 'l’était'} pas {libelleAvant(d, 'sur')}.</> })
  }
  if (d.kpis.biggest_ticket_cents !== null) {
    faits.push({ cle: 'gros', icone: <Coins size={16} />, texte: <>Le plus gros ticket encaissé : <strong>{montant(d.kpis.biggest_ticket_cents)}</strong>.</> })
  }
  return (
    <section className="lc-panel fsv-retenir" aria-labelledby="fsv-retenir-titre">
      <Tete icone={<Sparkles size={17} />} titre="À retenir" id="fsv-retenir-titre" />
      <div className="lc-panel__body">
        {faits.length === 0 ? <p className="lc-hint">Trop peu de ventes horodatées pour en tirer quelque chose.</p> : (
          <ul className="fsv-faits">
            {faits.map((f) => (
              <li key={f.cle} className="fsv-fait"><span className="fsv-fait__icone" aria-hidden="true">{f.icone}</span><span className="fsv-fait__texte">{f.texte}</span></li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

/* ═══════════════════════════════ l'affluence ═══════════════════════════════ */

interface Rangee {
  cle: string; titre: string; sous: string | null; lien: string | null
  cells: Cellule[]; tickets: number; cents: number
  /** 1 pour une nuit ; le nombre de nuits pour une moyenne par jour de semaine. */
  diviseur: number
}

/** Un plafond d'axe « rond » au-dessus de la valeur : 0 / 25 / 50, jamais 0 / 23,7 / 47,4. */
function plafond(v: number): number {
  if (v <= 0) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (v <= m * p) return m * p
  return 10 * p
}

function Affluence(props: { d: Ventes; vestiaire: boolean }) {
  const { d } = props
  const [vue, setVue] = useState<'soirees' | 'jours'>(d.by_night.length > 14 ? 'jours' : 'soirees')
  const [mesure, setMesure] = useState<Mesure>('tickets')
  const [rendu, setRendu] = useState<'graphique' | 'tableau'>('graphique')
  const unite = props.vestiaire ? 'passage' : 'ticket'
  return (
    <section className="lc-panel fsv-affluence" aria-labelledby="fsv-affluence-titre">
      <Tete icone={<BarChart3 size={17} />} titre="L’affluence, heure par heure" id="fsv-affluence-titre">
        <div className="fsv-seg">
          <SegmentedControl value={mesure} onChange={setMesure} size="sm" ariaLabel="Mesure de l’affluence"
            options={[{ value: 'tickets', label: props.vestiaire ? 'Passages' : 'Tickets' }, { value: 'montant', label: 'Encaissé' }]} />
          <SegmentedControl value={rendu} onChange={setRendu} size="sm" ariaLabel="Présentation de l’affluence"
            options={[{ value: 'graphique', label: 'Graphique' }, { value: 'tableau', label: 'Tableau' }]} />
        </div>
      </Tete>
      <div className="lc-panel__body fsv-affluence__corps">
        <div className="fsv-bloc">
          <div className="fsv-bloc__tete">
            <h3 className="fsv-bloc__titre">{vue === 'soirees' ? 'Soirée par soirée' : 'Par jour de la semaine, moyenne par nuit'}</h3>
            <div className="fsv-seg">
              <SegmentedControl value={vue} onChange={setVue} size="sm" ariaLabel="Lignes de la carte de chaleur"
                options={[{ value: 'soirees', label: 'Par soirée' }, { value: 'jours', label: 'Par jour de la semaine' }]} />
            </div>
          </div>
          <Chaleur d={d} vue={vue} mesure={mesure} rendu={rendu} unite={unite} />
        </div>
        <div className="fsv-bloc">
          <div className="fsv-bloc__tete">
            <h3 className="fsv-bloc__titre">La nuit type : moyenne par nuit, créneau par créneau</h3>
          </div>
          <Courbe d={d} mesure={mesure} rendu={rendu} unite={unite} />
        </div>
      </div>
      {d.untimed_tickets > 0 ? (
        <p className="lc-hint fsv-note">{pluriel(d.untimed_tickets, unite)} sans heure lue : hors des graphiques, comptés dans les totaux.</p>
      ) : null}
    </section>
  )
}

/**
 * LA CARTE DE CHALEUR — une ligne par soirée (ou par jour de semaine), une
 * colonne par heure de la nuit. Échelle séquentielle d'une seule teinte :
 * plus foncé = plus de ventes, cinq niveaux, l'échelle est affichée. Une case
 * vide se distingue d'une case faible : zéro n'est pas « peu ».
 */
function Chaleur(props: { d: Ventes; vue: 'soirees' | 'jours'; mesure: Mesure; rendu: 'graphique' | 'tableau'; unite: string }) {
  const { d, mesure } = props
  const [vise, setVise] = useState<{ r: number; c: number } | null>(null)
  const rangees: Rangee[] = props.vue === 'soirees'
    ? d.by_night.map((n) => ({
        cle: n.business_date, titre: nuitCourte(n.business_date), sous: n.soirees.length > 0 ? n.soirees.join(' · ') : null,
        lien: `/comptabilite/nuits/${n.business_date}`, cells: n.cells, tickets: n.tickets, cents: n.revenue_cents, diviseur: 1,
      }))
    : d.by_weekday.map((w) => ({
        cle: String(w.weekday), titre: JOURS[w.weekday] ?? '', sous: `moyenne de ${pluriel(w.nights, 'nuit')}`,
        lien: null, cells: w.cells, tickets: w.tickets, cents: w.revenue_cents, diviseur: w.nights,
      }))
  const valeur = (c: Cellule, r: Rangee): number => (mesure === 'tickets' ? c.tickets : c.revenue_cents) / r.diviseur
  const ecrire = (v: number, r: Rangee): string => (mesure === 'tickets'
    ? `${r.diviseur > 1 ? decimal(v) : nombre(v)} ${props.unite}${v >= 2 ? 's' : ''}`
    : montant(Math.round(v)))
  const ecrireTotal = (r: Rangee): string => (mesure === 'tickets'
    ? (r.diviseur > 1 ? decimal(r.tickets / r.diviseur) : nombre(r.tickets))
    : montant(Math.round(r.cents / r.diviseur)))

  let max = 0
  let plusForte: { r: number; c: number } | null = null
  for (let ri = 0; ri < rangees.length; ri++) {
    const r = rangees[ri]!
    for (let ci = 0; ci < r.cells.length; ci++) {
      const v = valeur(r.cells[ci]!, r)
      if (v > max) { max = v; plusForte = { r: ri, c: ci } }
    }
  }
  const niveau = (v: number): number => (v <= 0 || max <= 0 ? 0 : Math.min(5, Math.max(1, Math.ceil((v / max) * 5))))
  const lue: { r: number; c: number } | null = vise ?? plusForte

  if (rangees.length === 0) return <p className="lc-hint">Aucune vente horodatée sur la période.</p>

  if (props.rendu === 'tableau') {
    return (
      <div className="fi-tablewrap">
        <table className="fi-table fsv-table">
          <thead>
            <tr>
              <th scope="col">{props.vue === 'soirees' ? 'Nuit' : 'Jour'}</th>
              {d.slots.map((s) => <th key={s.hour} scope="col" className="fi-money">{s.label}</th>)}
              <th scope="col" className="fi-money">Total</th>
            </tr>
          </thead>
          <tbody>
            {rangees.map((r) => (
              <tr key={r.cle}>
                <th scope="row"><span className="fi-cell-titre">{r.titre}</span>{r.sous ? <span className="fi-cell-sous">{r.sous}</span> : null}</th>
                {r.cells.map((c, i) => (
                  <td key={i} className="fi-money">{valeur(c, r) === 0 ? '·' : mesure === 'tickets' ? (r.diviseur > 1 ? decimal(valeur(c, r)) : nombre(valeur(c, r))) : montant(Math.round(valeur(c, r)))}</td>
                ))}
                <td className="fi-money fi-money--fort">{ecrireTotal(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const rl = lue ? rangees[lue.r] : undefined
  const cl = lue && rl ? rl.cells[lue.c] : undefined
  return (
    <div className="fsv-chaleur" style={{ '--fsv-cols': d.slots.length } as CSSProperties} onMouseLeave={() => setVise(null)}>
      <p className="fsv-lecture" aria-live="polite">
        {lue && rl && cl ? (
          <>
            <strong>{rl.titre} · {d.slots[lue.c]!.label}</strong>
            <span>{ecrire(valeur(cl, rl), rl)}{mesure === 'tickets' ? ` · ${montant(Math.round(cl.revenue_cents / rl.diviseur))}` : ''}</span>
            {vise === null ? <span className="fsv-lecture__aide">la case la plus chargée — survolez une case pour lire les autres</span> : null}
          </>
        ) : <span className="fsv-lecture__aide">Survolez une case pour lire sa valeur.</span>}
      </p>
      <p className="fsv-chaleur__glisser">Faites glisser la carte pour voir toutes les heures de la nuit.</p>
      <div className="fsv-chaleur__grille" role="table" aria-label="Affluence par heure de la nuit">
        <div className="fsv-chaleur__rang fsv-chaleur__rang--tete" role="row">
          <span className="fsv-chaleur__coin" role="columnheader">{props.vue === 'soirees' ? 'Nuit' : 'Jour'}</span>
          {d.slots.map((s) => (
            <span key={s.hour} role="columnheader" className="fsv-chaleur__heure">{s.label}</span>
          ))}
          <span className="fsv-chaleur__total fsv-chaleur__total--tete" role="columnheader">{mesure === 'tickets' ? 'Total' : 'Encaissé'}</span>
        </div>
        {rangees.map((r, ri) => (
          <div key={r.cle} className="fsv-chaleur__rang" role="row">
            <span className="fsv-chaleur__titre" role="rowheader">
              {r.lien ? <Link href={r.lien}>{r.titre}</Link> : r.titre}
              {r.sous ? <span className="fsv-chaleur__sous">{r.sous}</span> : null}
            </span>
            {r.cells.map((c, ci) => {
              const v = valeur(c, r)
              const estVise = lue !== null && lue.r === ri && lue.c === ci
              return (
                <span
                  key={ci}
                  role="cell"
                  className={`fsv-case fsv-case--${niveau(v)}${estVise ? ' fsv-case--vise' : ''}`}
                  aria-label={`${r.titre}, ${d.slots[ci]!.label} : ${v === 0 ? 'aucune vente' : ecrire(v, r)}`}
                  onMouseEnter={() => setVise({ r: ri, c: ci })}
                />
              )
            })}
            <span className="fsv-chaleur__total" role="cell">{ecrireTotal(r)}</span>
          </div>
        ))}
      </div>
      <div className="fsv-echelle" aria-hidden="true">
        <span className="fsv-echelle__mot">aucune vente</span>
        <span className="fsv-case fsv-case--0 fsv-echelle__case" />
        <span className="fsv-echelle__mot">peu</span>
        {[1, 2, 3, 4, 5].map((n) => <span key={n} className={`fsv-case fsv-case--${n} fsv-echelle__case`} />)}
        <span className="fsv-echelle__mot">beaucoup — la case la plus foncée vaut {plusForte && rangees[plusForte.r] ? ecrire(max, rangees[plusForte.r]!) : '—'}</span>
      </div>
    </div>
  )
}

/**
 * LA NUIT TYPE — la moyenne par nuit, créneau par créneau, et la même moyenne
 * sur la période précédente en gris : deux séries, une légende, un seul axe.
 * Le tracé est en SVG étiré (aucun texte dedans, le trait ne s'étire pas) ;
 * les étiquettes, le repère et les points sont en HTML, au plancher de 13 px.
 */
function Courbe(props: { d: Ventes; mesure: Mesure; rendu: 'graphique' | 'tableau'; unite: string }) {
  const { d, mesure } = props
  const [vise, setVise] = useState<number | null>(null)
  const n = d.slots.length
  const nuits = Math.max(1, d.kpis.nights_with_sales)
  const nuitsAvant = d.previous_kpis && d.previous_kpis.nights_with_sales > 0 ? d.previous_kpis.nights_with_sales : null
  const points = d.curve.map((c) => ({
    label: c.label,
    v: (mesure === 'tickets' ? c.tickets : c.revenue_cents) / nuits,
    avant: nuitsAvant === null || c.previous_tickets === null || c.previous_revenue_cents === null
      ? null : (mesure === 'tickets' ? c.previous_tickets : c.previous_revenue_cents) / nuitsAvant,
  }))
  const avecAvant = points.some((p) => p.avant !== null)
  const haut = plafond(Math.max(0, ...points.map((p) => p.v), ...points.map((p) => p.avant ?? 0)))
  const ecrire = (v: number): string => (mesure === 'tickets' ? `${decimal(v)} ${props.unite}${v >= 2 ? 's' : ''}` : montant(Math.round(v)))
  const ecrireAxe = (v: number): string => (mesure === 'tickets' ? decimal(v) : formatMoney(Math.round(v), { compact: true }).replace(/ (k?€)$/, ' $1'))
  const x = (i: number): number => ((i + 0.5) / n) * 1000
  const y = (v: number): number => 100 - (v / haut) * 100
  const trace = (vals: number[]): string => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(2)}`).join(' ')
  const ligne = trace(points.map((p) => p.v))
  const aire = `${ligne} L${x(n - 1).toFixed(1)},100 L${x(0).toFixed(1)},100 Z`
  const ligneAvant = avecAvant ? trace(points.map((p) => p.avant ?? 0)) : null
  let pic = 0
  points.forEach((p, i) => { if (p.v > points[pic]!.v) pic = i })
  const lu = vise ?? pic
  const pl = points[lu]
  const contre = libelleAvant(d, 'nu')
  const contreSur = libelleAvant(d, 'sur')

  if (props.rendu === 'tableau') {
    return (
      <div className="fi-tablewrap">
        <table className="fi-table fsv-table">
          <thead>
            <tr><th scope="col">Heure</th><th scope="col" className="fi-money">Par nuit</th>{avecAvant ? <th scope="col" className="fi-money">Avant</th> : null}</tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.label}>
                <th scope="row">{p.label}</th>
                <td className="fi-money">{ecrire(p.v)}</td>
                {avecAvant ? <td className="fi-money">{p.avant === null ? '—' : ecrire(p.avant)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="fsv-courbe" style={{ '--fsv-cols': n } as CSSProperties}>
      <p className="fsv-lecture" aria-live="polite">
        {pl ? (
          <>
            <strong>{pl.label}</strong>
            <span>{ecrire(pl.v)} par nuit</span>
            {pl.avant !== null ? <span className="fsv-lecture__avant">{ecrire(pl.avant)} par nuit {contreSur}</span> : null}
            {vise === null ? <span className="fsv-lecture__aide">le créneau le plus fort — survolez la courbe pour lire les autres</span> : null}
          </>
        ) : null}
      </p>
      <div className="fsv-courbe__cadre">
        <div className="fsv-courbe__axe-y" aria-hidden="true">
          <span>{ecrireAxe(haut)}</span><span>{ecrireAxe(haut / 2)}</span><span>0</span>
        </div>
        <div className="fsv-courbe__trace" onMouseLeave={() => setVise(null)}>
          <svg className="fsv-courbe__svg" viewBox="0 0 1000 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <path d={aire} className="fsv-courbe__aire" />
            {ligneAvant ? <path d={ligneAvant} className="fsv-courbe__ligne fsv-courbe__ligne--avant" /> : null}
            <path d={ligne} className="fsv-courbe__ligne" />
          </svg>
          {pl ? (
            <>
              <span className="fsv-courbe__repere" style={{ left: `${x(lu) / 10}%` }} aria-hidden="true" />
              {pl.avant !== null ? <span className="fsv-courbe__point fsv-courbe__point--avant" style={{ left: `${x(lu) / 10}%`, bottom: `${(pl.avant / haut) * 100}%` }} aria-hidden="true" /> : null}
              <span className="fsv-courbe__point" style={{ left: `${x(lu) / 10}%`, bottom: `${(pl.v / haut) * 100}%` }} aria-hidden="true" />
            </>
          ) : null}
          <div className="fsv-courbe__cibles">
            {points.map((p, i) => (
              <button
                key={p.label}
                type="button"
                className="fsv-courbe__cible"
                aria-label={`${p.label} : ${ecrire(p.v)} par nuit${p.avant !== null ? `, ${ecrire(p.avant)} par nuit ${contreSur}` : ''}`}
                onMouseEnter={() => setVise(i)}
                onFocus={() => setVise(i)}
                onBlur={() => setVise(null)}
              />
            ))}
          </div>
        </div>
        <span aria-hidden="true" />
        <div className="fsv-courbe__axe-x" aria-hidden="true">
          {points.map((p, i) => <span key={p.label} className={i % 2 === 1 ? 'fsv-courbe__tick--impaire' : undefined}>{p.label}</span>)}
        </div>
      </div>
      <ul className="fsv-legende">
        <li><span className="fsv-legende__trait" aria-hidden="true" />la période affichée</li>
        {avecAvant ? <li><span className="fsv-legende__trait fsv-legende__trait--avant" aria-hidden="true" />{contre}</li> : (
          <li className="fsv-legende__absent">rien à comparer : aucune vente lue {contreSur}</li>
        )}
      </ul>
    </div>
  )
}

/* ═══════════════════════════════ le classement des produits ═══════════════════════════════ */

const PREMIERS = 25

function Classement(props: { d: Ventes; voitOfferts: boolean }) {
  const { d } = props
  const [q, setQ] = useState('')
  const [tri, setTri] = useState<Tri>('qty')
  const [tous, setTous] = useState(false)
  const cherche = q.trim().toLocaleLowerCase('fr')
  // Le rang est celui de la période (en quantité) : chercher « shooter » ne fait pas de lui le n° 1.
  const rang = useMemo(() => new Map(d.products.map((p, i) => [p.product, i + 1])), [d.products])
  const tries = useMemo(() => {
    const l = [...d.products]
    const evo = (p: Produit): number => (p.is_new ? Number.POSITIVE_INFINITY : p.qty_change ?? Number.NEGATIVE_INFINITY)
    if (tri === 'montant') l.sort((a, b) => (b.revenue_cents ?? -1) - (a.revenue_cents ?? -1) || b.qty - a.qty)
    else if (tri === 'evolution') l.sort((a, b) => evo(b) - evo(a) || b.qty - a.qty)
    else if (tri === 'nom') l.sort((a, b) => a.product.localeCompare(b.product, 'fr'))
    return l
  }, [d.products, tri])
  const filtres = tries.filter((p) => cherche === '' || p.product.toLocaleLowerCase('fr').includes(cherche))
  const visibles = tous || cherche !== '' ? filtres : filtres.slice(0, PREMIERS)
  const max = Math.max(1, ...d.products.map((p) => (tri === 'montant' ? p.revenue_cents ?? 0 : p.qty)))
  const comparable = d.previous.has_data

  return (
    <section className="lc-panel fsv-classement" aria-labelledby="fsv-classement-titre">
      <Tete icone={<Package size={17} />} titre="Tous les produits" id="fsv-classement-titre">
        <span className="lc-hint">{pluriel(d.products.length, 'référence')} · {pluriel(d.kpis.articles, 'article')}</span>
      </Tete>
      <div className="lc-panel__body">
        <div className="fsv-filtres">
          <label className="fsv-recherche">
            <Search size={15} aria-hidden="true" />
            <input className="lc-input" placeholder="Chercher un produit" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Chercher un produit" />
          </label>
          <div className="fsv-seg">
            <SegmentedControl value={tri} onChange={setTri} size="sm" ariaLabel="Ordre des produits"
              options={[
                { value: 'qty', label: 'Quantité' }, { value: 'montant', label: 'Encaissé' },
                ...(comparable ? [{ value: 'evolution' as Tri, label: 'Évolution' }] : []),
                { value: 'nom', label: 'Nom' },
              ]} />
          </div>
        </div>
        {d.products.length === 0 ? <p className="lc-hint">Aucun produit relevé sur la période.</p> : filtres.length === 0 ? (
          <p className="lc-hint">Aucun produit ne contient « {q.trim()} ».</p>
        ) : (
          <>
            <div className="fsv-produit fsv-produit--entete" aria-hidden="true">
              <span /><span>Produit</span><span>Part des articles</span>
              <span className="fsv-produit__qte">Quantité</span><span className="fsv-produit__montant">Encaissé</span>
              <span className="fsv-produit__evo">{comparable ? 'Évolution' : ''}</span>
            </div>
            <ol className="fsv-produits">
              {visibles.map((p) => {
                const r = rang.get(p.product) ?? 0
                const part = tri === 'montant' ? p.revenue_cents ?? 0 : p.qty
                const details = [
                  p.unit_prices_cents.length > 0 ? `${p.unit_prices_cents.map((c) => montant(c)).join(' puis ')} l’unité` : null,
                  p.dosed_qty > 0 ? `${nombre(p.dosed_qty)} au doseur` : null,
                  props.voitOfferts && p.non_revenue_cents !== null && p.non_revenue_cents > 0 ? `${montant(p.non_revenue_cents)} offerts` : null,
                  p.previous_qty !== null && !p.is_new ? `avant : ${nombre(p.previous_qty)}` : null,
                ].filter((x): x is string => x !== null)
                return (
                  <li key={p.product} className={`fsv-produit${r <= 3 ? ' fsv-produit--podium' : ''}`}>
                    <span className="fsv-produit__rang" aria-label={`rang ${r}`}>{r}</span>
                    <span className="fsv-produit__nom">
                      {p.product}
                      {details.length > 0 ? <span className="fsv-produit__sous">{details.join(' · ')}</span> : null}
                    </span>
                    <span className="fsv-produit__part">
                      <span className="fsv-produit__piste" aria-hidden="true">
                        <span className="fsv-produit__barre" style={{ width: `${Math.max(2, Math.round((part / max) * 100))}%` }} />
                      </span>
                      <span className="fsv-produit__pourcent">{pourcent(p.share_qty)}</span>
                    </span>
                    <span className="fsv-produit__qte">{nombre(p.qty)}</span>
                    <span className="fsv-produit__montant fi-money">{montant(p.revenue_cents)}</span>
                    <span className="fsv-produit__evo"><Evolution p={p} /></span>
                  </li>
                )
              })}
            </ol>
            {visibles.length < filtres.length ? (
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm fsv-plus" onClick={() => setTous(true)}>
                Afficher les {pluriel(filtres.length - visibles.length, 'autre produit', 'autres produits')}
              </button>
            ) : null}
          </>
        )}
      </div>
    </section>
  )
}

/* ═══════════════════════════════ paiements, offerts ═══════════════════════════════ */

function Paiements(props: { d: Ventes; voitOfferts: boolean }) {
  const { d } = props
  const max = Math.max(1, ...d.payments.map((m) => m.cents))
  return (
    <section className="lc-panel fsv-paiements" aria-labelledby="fsv-paiements-titre">
      <Tete icone={<CreditCard size={17} />} titre="Moyens de paiement" id="fsv-paiements-titre" />
      <div className="lc-panel__body">
        {d.payments.length === 0 ? <p className="lc-hint">Aucun paiement relevé sur la période.</p> : (
          <ul className="fsv-barres">
            {d.payments.map((m) => (
              <li key={m.label} className={`fsv-barre-ligne${m.revenue === false ? ' fsv-barre-ligne--hors' : ''}`}>
                <span className="fsv-barre-ligne__nom">
                  {m.label}
                  <span className="fsv-barre-ligne__sous">
                    {m.revenue === false ? 'offerts et remises : hors encaissé' : `${pourcent(m.share)} de l’encaissé`} · {pluriel(m.tickets, 'ticket')}
                  </span>
                </span>
                <span className="fsv-barre-ligne__piste" aria-hidden="true"><span className="fsv-barre-ligne__barre" style={{ width: `${Math.max(2, Math.round((m.cents / max) * 100))}%` }} /></span>
                <span className="fsv-barre-ligne__val fi-money">{montant(m.cents)}</span>
              </li>
            ))}
          </ul>
        )}
        {!props.voitOfferts ? <p className="lc-hint fsv-remarque">La carte serveur n’apparaît pas : votre rôle ne permet pas de lire les offerts.</p> : null}
      </div>
    </section>
  )
}

function Offerts(props: { d: Ventes; voitOfferts: boolean }) {
  const o = props.d.offers
  const max = Math.max(1, ...(o?.by_product ?? []).map((p) => p.cents))
  return (
    <section className="lc-panel fsv-offerts" aria-labelledby="fsv-offerts-titre">
      <Tete icone={<Gift size={17} />} titre="Offerts, produit par produit" id="fsv-offerts-titre">
        {props.voitOfferts && o ? <span className="fsv-tete__total fi-money">{montant(o.total_cents)}</span> : null}
      </Tete>
      <div className="lc-panel__body">
        {!props.voitOfferts || !o ? (
          <p className="lc-hint">Votre rôle ne permet pas de lire les offerts : ils ne sont pas transmis à cet écran.</p>
        ) : o.by_product.length === 0 ? (
          <p className="lc-hint">Aucun produit réglé par carte serveur sur la période.</p>
        ) : (
          <>
            <p className="fsv-offerts__resume">{pluriel(o.tickets, 'ticket réglé', 'tickets réglés')} en tout ou partie par carte serveur. La part offerte de chaque produit :</p>
            <ul className="fsv-barres">
              {o.by_product.map((p) => (
                <li key={p.product} className="fsv-barre-ligne">
                  <span className="fsv-barre-ligne__nom">
                    {p.product}
                    {p.share !== null ? <span className="fsv-barre-ligne__sous">{pourcent(p.share)} de ce produit offert</span> : null}
                  </span>
                  <span className="fsv-barre-ligne__piste" aria-hidden="true"><span className="fsv-barre-ligne__barre fsv-barre-ligne__barre--doux" style={{ width: `${Math.max(2, Math.round((p.cents / max) * 100))}%` }} /></span>
                  <span className="fsv-barre-ligne__val fi-money">{montant(p.cents)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  )
}

/* ═══════════════════════════════ les salles ═══════════════════════════════ */

function Salles(props: { d: Ventes; vestiaire: boolean }) {
  const { d } = props
  const maxPdv = Math.max(1, ...d.rooms.flatMap((r) => r.points_of_sale.map((p) => p.revenue_cents)))
  return (
    <section className="lc-panel fsv-salles" aria-labelledby="fsv-salles-titre">
      <Tete icone={<Store size={17} />} titre="Salle par salle" id="fsv-salles-titre">
        <span className="lc-hint">l’encaissé de chaque point de vente, et ses caisses</span>
      </Tete>
      <div className="lc-panel__body">
        {d.rooms.length === 0 ? <p className="lc-hint">Aucune vente rattachée à une salle sur la période.</p> : (
          <div className="fsv-salles__grille">
            {d.rooms.map((r) => (
              <article key={r.location_id} className="fsv-salle">
                <header className="fsv-salle__tete">
                  <div className="fsv-salle__ident">
                    <h3 className="fsv-salle__nom">{r.name}</h3>
                    <span className="fsv-salle__faits">{pluriel(r.tickets, props.vestiaire ? 'passage' : 'ticket')} · {pluriel(r.articles, 'article')}</span>
                  </div>
                  <div className="fsv-salle__total">
                    <span className="fsv-salle__montant">{montant(r.revenue_cents)}</span>
                    <span className="fsv-salle__part">{pourcent(r.share_revenue)} de l’encaissé</span>
                  </div>
                </header>
                {r.star ? (
                  <p className="fsv-salle__star"><Trophy size={14} aria-hidden="true" />Produit star : <strong>{r.star.product}</strong> ({pluriel(r.star.qty, 'vendu', 'vendus')})</p>
                ) : null}
                <ul className="fsv-pdvs">
                  {r.points_of_sale.map((p) => (
                    <li key={p.id} className="fsv-pdv">
                      <div className="fsv-pdv__ligne">
                        <span className="fsv-pdv__nom">{p.name}</span>
                        <span className="fsv-pdv__piste" aria-hidden="true"><span className="fsv-pdv__barre" style={{ width: `${Math.max(2, Math.round((p.revenue_cents / maxPdv) * 100))}%` }} /></span>
                        <span className="fsv-pdv__val fi-money">{montant(p.revenue_cents)}</span>
                      </div>
                      <p className="fsv-pdv__faits">
                        {pourcent(p.share_revenue)} de l’encaissé · panier moyen {montant(p.average_ticket_cents)} · {pluriel(p.tickets, 'ticket')}
                      </p>
                      {p.registers.length > 1 ? (
                        <ul className="fsv-caisses">
                          {p.registers.map((c) => (
                            <li key={c.register_id}>
                              <span className="fsv-caisses__nom">{c.name}</span>
                              <span className="fsv-caisses__val">{montant(c.revenue_cents)} · {pluriel(c.tickets, 'ticket')}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

/* ═══════════════════════════════ les litres servis ═══════════════════════════════ */

function Spiritueux(props: { d: Ventes; vestiaire: boolean }) {
  // Les doseurs n'équipent que les bars : au vestiaire, il n'y a rien à dire.
  if (props.vestiaire) return null
  const s = props.d.spirits
  const max = Math.max(0.001, ...(s?.products ?? []).map((p) => p.volume_l ?? 0))
  return (
    <section className="lc-panel fsv-spiritueux" aria-labelledby="fsv-spiritueux-titre">
      <Tete icone={<GlassWater size={17} />} titre="L’alcool servi au doseur, par spiritueux" id="fsv-spiritueux-titre">
        {s ? <span className="fsv-tete__total">{litres(s.total_volume_l)}</span> : null}
      </Tete>
      <div className="lc-panel__body">
        {!s ? (
          <p className="lc-hint">Aucune annexe « Doseurs » reçue pour cette période : les litres servis ne peuvent pas être lus.</p>
        ) : (
          <>
            <p className="fsv-spiritueux__resume">
              {pluriel(s.total_doses, 'dose servie', 'doses servies')} par {pluriel(s.rampes, 'rampe')} sur {pluriel(s.nights, 'nuit')}.
              {!s.complete ? ' Une rampe ne donne ni le volume ni la taille de dose de certains produits : leurs litres sont inconnus et ne comptent pas dans le total.' : ''}
            </p>
            <ul className="fsv-barres">
              {s.products.map((p) => (
                <li key={p.product} className="fsv-barre-ligne">
                  <span className="fsv-barre-ligne__nom">
                    {p.product}
                    <span className="fsv-barre-ligne__sous">
                      {pluriel(p.doses, 'dose')}{p.dose_cl.length > 0 ? ` de ${p.dose_cl.map((c) => `${c.toLocaleString('fr-FR')} cl`).join(' ou ')}` : ''}
                      {p.bottle_changes > 0 ? ` · ${pluriel(p.bottle_changes, 'bouteille changée', 'bouteilles changées')}` : ''}
                    </span>
                  </span>
                  <span className="fsv-barre-ligne__piste" aria-hidden="true">
                    {p.volume_l !== null ? <span className="fsv-barre-ligne__barre" style={{ width: `${Math.max(2, Math.round((p.volume_l / max) * 100))}%` }} /> : null}
                  </span>
                  <span className="fsv-barre-ligne__val">{p.volume_l === null ? '—' : litres(p.volume_l)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  )
}
